/**
 * ContractAmendmentsEditor — F20 T20.17
 *
 * Wyświetla listę aneksów/zmian do umowy oraz (gdy canManage=true)
 * formularz dołączenia nowego aneksu. Endpoint jest append-only
 * (brak PATCH i DELETE) — lista jest tylko do odczytu.
 *
 * Kontrakt API:
 *  GET  /contracts/:id/amendments  → { amendments: Amendment[] } asc createdAt
 *  POST /contracts/:id/amendments  → 201 { id }
 *  404 CONTRACT_NOT_FOUND → traktujemy jako pustą listę (model B masking)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Amendment {
  id: string;
  documentId: string;
  changeType: string | null;
  changeDate: string | null;
  comment: string | null;
  createdByUserId: string;
  createdAt: string;
}

interface AmendmentsResponse {
  amendments: Amendment[];
}

interface AddAmendmentBody {
  changeType: null;
  changeDate: string | null;
  comment: string;
}

// ─── Helper — detect 404 (model B masking) ──────────────────────────────────

function isNotFound(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    'response' in err &&
    err.response !== null &&
    typeof err.response === 'object' &&
    'status' in err.response &&
    err.response.status === 404
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export interface ContractAmendmentsEditorProps {
  documentId: string;
  /** When false — read-only view (list only, no add form). */
  canManage: boolean;
}

export function ContractAmendmentsEditor({ documentId, canManage }: ContractAmendmentsEditorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const QUERY_KEY = ['contract-amendments', documentId] as const;

  const [showAddForm, setShowAddForm] = useState(false);
  const [dateValue, setDateValue] = useState('');
  const [commentValue, setCommentValue] = useState('');

  // ── Fetch ──
  const { data, isLoading, isError } = useQuery<AmendmentsResponse>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await api.get<AmendmentsResponse>('/contracts/' + documentId + '/amendments');
        return res.data;
      } catch (err: unknown) {
        if (isNotFound(err)) {
          return { amendments: [] };
        }
        throw err;
      }
    },
    enabled: !!documentId,
    staleTime: 30_000,
  });

  const amendments = data?.amendments ?? [];

  // ── Invalidate ──
  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  // ── Add mutation ──
  const addMutation = useMutation({
    mutationFn: async (body: AddAmendmentBody) => {
      const res = await api.post<{ id: string }>('/contracts/' + documentId + '/amendments', body);
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.contracts.amendments.toast.addSuccess'),
      });
      resetForm();
      setShowAddForm(false);
      invalidate();
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.documents.contracts.amendments.toast.addError'),
      });
    },
  });

  function resetForm() {
    setDateValue('');
    setCommentValue('');
  }

  function handleCancel() {
    resetForm();
    setShowAddForm(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (commentValue.trim() === '') return;
    const body: AddAmendmentBody = {
      changeType: null,
      changeDate: dateValue.trim() !== '' ? dateValue : null,
      comment: commentValue.trim(),
    };
    addMutation.mutate(body);
  }

  // ── Format helpers ──
  function formatChangeDate(value: string | null): string {
    if (!value) return '—';
    return value; // already "YYYY-MM-DD"
  }

  function formatCreatedAt(value: string): string {
    try {
      return new Date(value).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return value;
    }
  }

  // ── Render states ──
  const inputClass =
    'w-full rounded-lg border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:bg-bg-secondary disabled:text-text-tertiary';
  const labelClass = 'block text-xs font-medium text-text-secondary mb-1';

  if (isLoading) {
    return (
      <div
        className="animate-pulse space-y-2"
        data-testid="contract-amendments-loading"
        aria-busy="true"
      >
        <div className="h-8 rounded bg-bg-secondary" />
        <div className="h-8 rounded bg-bg-secondary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700"
        data-testid="contract-amendments-error"
      >
        {t('tenant.documents.contracts.amendments.loadError')}
      </div>
    );
  }

  // ── Main render ──
  return (
    <section data-testid="contract-amendments-editor" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          {t('tenant.documents.contracts.amendments.title')}
        </h3>
        {canManage && !showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            data-testid="add-amendment-open-btn"
            className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700"
          >
            {t('tenant.documents.contracts.amendments.addButton')}
          </button>
        )}
      </div>

      {/* Amendment list */}
      {amendments.length === 0 ? (
        <p className="text-sm text-text-muted" data-testid="contract-amendments-empty">
          {t('tenant.documents.contracts.amendments.empty')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="contract-amendments-list">
          {amendments.map((amendment) => (
            <li
              key={amendment.id}
              data-testid={`amendment-row-${amendment.id}`}
              className="rounded-lg border border-border-primary bg-bg-primary p-3"
            >
              <div className="flex flex-wrap items-start gap-4">
                <div className="min-w-0 flex-1 space-y-1">
                  {amendment.comment && (
                    <p
                      className="text-sm text-text-primary"
                      data-testid={`amendment-comment-${amendment.id}`}
                    >
                      {amendment.comment}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-4 text-xs text-text-muted">
                    <span data-testid={`amendment-date-${amendment.id}`}>
                      {t('tenant.documents.contracts.amendments.fields.date')}:{' '}
                      {formatChangeDate(amendment.changeDate)}
                    </span>
                    <span data-testid={`amendment-created-at-${amendment.id}`}>
                      {t('tenant.documents.contracts.amendments.fields.addedOn')}:{' '}
                      {formatCreatedAt(amendment.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add form (shown inline below list) */}
      {canManage && showAddForm && (
        <form
          onSubmit={handleSubmit}
          data-testid="add-amendment-form"
          className="rounded-lg border border-border-primary bg-bg-secondary p-4 space-y-4"
        >
          <p className="text-sm font-semibold text-text-primary">
            {t('tenant.documents.contracts.amendments.addTitle')}
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* Date */}
            <div>
              <label htmlFor="add-amendment-date" className={labelClass}>
                {t('tenant.documents.contracts.amendments.fields.date')}
              </label>
              <Input
                type="date"
                id="add-amendment-date"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
                data-testid="add-amendment-date"
              />
            </div>
          </div>

          {/* Comment */}
          <div>
            <label htmlFor="add-amendment-comment" className={labelClass}>
              {t('tenant.documents.contracts.amendments.fields.comment')}
              <span className="ml-0.5 text-red-500">*</span>
            </label>
            <textarea
              id="add-amendment-comment"
              value={commentValue}
              onChange={(e) => setCommentValue(e.target.value)}
              data-testid="add-amendment-comment"
              rows={3}
              required
              className={inputClass + ' resize-y'}
              placeholder={t('tenant.documents.contracts.amendments.fields.commentPlaceholder')}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={addMutation.isPending || commentValue.trim() === ''}
              data-testid="add-amendment-submit"
              className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-60"
            >
              {addMutation.isPending
                ? t('tenant.documents.contracts.amendments.adding')
                : t('tenant.documents.contracts.amendments.submitButton')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
